import { DateOrTimeView, DatePicker, DateTimePicker, DateTimeValidationError, DateValidationError, DateView, PickerChangeHandlerContext, TimeView } from "@mui/x-date-pickers";
import { getMainColors } from "constants/colors";
import { format } from "date-fns";
import { useToggle } from "hooks";
import { useEffect, useRef, useState } from "react";
import { FormLabel } from "react-bootstrap";
import { TooltipIcon } from "./Tooltip";
import { ZLabel } from "./ZLabel";
import { ZTooltip } from "./ZTooltip";

type ZDateTimePickerProps = {
    value: Date | null;
    hasMonth?: boolean;
    hasDate?: boolean;
    minDate?: Date;
    maxDate?: Date;
    label?: string;
    hasTime?: boolean;
    disabled?: boolean;
    canClear?: boolean;
    isOnPopup?: boolean;
    nullable?: boolean;
    error?: string | null;
    onError?: (error: string | null) => void;
    onChanged?: (newDate: Date | null) => void;
}

export const ZDateTimePicker = ({ value, minDate, maxDate, label, hasTime, hasMonth = true, hasDate = true, canClear = true, isOnPopup = false, disabled, nullable, error, onError, onChanged }: ZDateTimePickerProps) => {
    const [cleared, setCleared] = useState(false);
    const globalMinDate = new Date(1990, 0, 1);
    const globalMaxDate = new Date(2040, 11, 31);

    const [dateTimePickerView, setDateTimePickerView] = useState<DateOrTimeView[] | undefined>(undefined);
    const [openToDateTime, setOpenToDateTime] = useState<DateOrTimeView | undefined>("hours");
    const [datePickerView, setDatePickerView] = useState<DateView[] | undefined>(undefined);  
    const [openToDate, setOpenToDate] = useState<DateView | undefined>(undefined);
    const [rerender, toggleRerender] = useToggle();
    
    useEffect(() => {
        if (cleared) {
          const timeout = setTimeout(() => {
            setCleared(false);
          }, 1500);
    
          return () => clearTimeout(timeout);
        }
        return () => {};
      }, [cleared]);

    const styling = {
        flexGrow: 1,
        "& .MuiInputBase-root": {
            fontSize: "0.8rem",
            padding: "0.5rem",
            paddingRight: "1.2rem",
            "& .MuiButtonBase-root": {
                padding: 0,
                paddingLeft: "0.1rem",
                marginLeft: 0,
            },
            "& .MuiInputBase-input": {
                padding: 0,
                paddingLeft: 0,
                
            },
        }        
    };

	const handleClear = (e: React.MouseEvent<Element, MouseEvent>) => {
		setCleared(true);
		onChanged && onChanged(null);
	};

	useEffect(() => {
		if (hasTime) {
			setDateTimePickerView(getDateTimePickerView());
			setOpenToDateTime(hasTime ? "hours" : hasDate ? "day" : hasMonth ? "month" : "year");
		} else {
			setDatePickerView(getDatePickerView());
			// console.log(hasDate ? "day" : hasMonth ? "month": "year")
			setOpenToDate(hasDate ? "day" : hasMonth ? "month" : "year");
		}
		toggleRerender();
	}, [hasTime, hasDate, hasMonth]);

	function getDateTimePickerView(): DateOrTimeView[] {
		return ["year", ...(hasMonth ? ["month" as DateView] : []), ...(hasDate ? ["day" as DateView] : []), ...(hasTime ? (["hours", "minutes"] as TimeView[]) : [])];
	}
	function getDatePickerView(): DateView[] {
		return ["year", ...(hasMonth ? ["month" as DateView] : []), ...(hasDate ? ["day" as DateView] : [])];
	}

	function runOnChanged(
		val: Date | null,
		context: PickerChangeHandlerContext<DateValidationError> | PickerChangeHandlerContext<DateTimeValidationError>,
		error?: DateValidationError | DateTimeValidationError
	) {
		if (nullable || (val !== null && !isNaN(val.getTime()))) {
			onChanged && onChanged(val);

			if (!context.validationError) {
				onError && onError(null);
			} else {
				if (error == "invalidDate") {
					onError && onError(`${hasTime ? "Invalid date and time" : "Invalid date"}`);
				} else if ((val && maxDate && val > maxDate) || (maxDate && error == "maxDate")) {
					onError && onError(`${hasTime ? "Date and time" : "Date"} must be before ${format(maxDate, `dd/MM/yyyy${hasTime ? "  hh:mm aa" : ""}`)}`);
				} else if ((val && minDate && val < minDate) || (minDate && error == "minDate")) {
					onError && onError(`${hasTime ? "Date and time" : "Date"} must be after ${format(minDate, `dd/MM/yyyy${hasTime ? "  hh:mm aa" : ""}`)}`);
				} else {
					onError && onError(null);
				}
			}
		} else {
			onError && onError(`${hasTime ? "Date and time" : "Date"} cannot be empty!`);
		}
	}

    return (
        <div className="date-time-picker-container">
            {(label ? <ZLabel>{label}: </ZLabel> : <></>)}
            <ZTooltip enabled={error != null} text={error ?? ""}>
                {hasTime ? 
                <DateTimePicker
                className={error != null ? "invalid" : ""}
                views={dateTimePickerView}
                disabled={disabled}
                minDateTime={minDate ?? globalMinDate} 
                maxDateTime={maxDate ?? globalMaxDate}
                openTo={openToDateTime}
                reduceAnimations
                value={value}
                timeSteps={{minutes: 1}}
                minutesStep={1}
                     
                slotProps={{                   
                    field: { clearable: canClear, onClear: (e) => handleClear(e) }, 
                    textField: { placeholder: `Enter date${hasTime ? " & time" : ""}` },
                    actionBar: {
                      actions: ['accept', 'today'],
                    },
                    popper: {
                        disablePortal: isOnPopup,
                        modifiers: [
                            {
                                name: 'zIndex',
                                enabled: true,
                                phase: 'write',
                                fn: ({ state }) => {
                                  state.styles.popper.zIndex = '99999';
                                },
                              },
                        ]
                    }
                }}
                onChange={(val, context) => {
                    runOnChanged(val, context);
                }}
                onError={(error, val) => {
                    const context: PickerChangeHandlerContext<any> = {
                        validationError: true
                    }
                    runOnChanged(val, context, error);
                }}                
                sx={styling} format={`${hasDate ? "dd/" : ""}${hasMonth ? "MM/" : ""}yyyy${hasTime ? " | hh:mm aa" : ""}`} formatDensity="spacious"
            />: 
                <DatePicker
                    className={error != null ? "invalid" : ""}
                    views={datePickerView}
                    disabled={disabled}
                    minDate={minDate ?? globalMinDate} 
                    maxDate={maxDate ?? globalMaxDate}
                    openTo={openToDate}
                    reduceAnimations                    
                    value={value}
                    slotProps={{                              
                        field: { clearable: canClear, onClear: (e) => handleClear(e) }, 
                        textField: { placeholder: `Enter date${hasTime ? " & time" : ""}` },
                        actionBar: {
                          actions: ['accept', 'today'],
                        },
                        popper: {
                            disablePortal: isOnPopup,
                            modifiers: [
                                {
                                    name: 'zIndex',
                                    enabled: true,
                                    phase: 'write',
                                    fn: ({ state }) => {
                                      state.styles.popper.zIndex = '99999';
                                    },
                                  },
                            ],                            
                        }
                    }}                    
                    onChange={(val, context) => {
                        runOnChanged(val, context);
                    }}
                    onError={(error, val) => {
                        const context: PickerChangeHandlerContext<any> = {
                            validationError: true
                        }
                        runOnChanged(val, context, error);
                    }}
                   
                    sx={styling} format={`${hasDate ? "dd/" : ""}${hasMonth ? "MM/" : ""}yyyy${hasTime ? " | hh:mm aa" : ""}`} formatDensity="spacious"
                    
                />}
            </ZTooltip>
        </div>
    );
};
